import { Sparkles, Droplets, BadgeEuro, Check, ArrowRight, Star } from "lucide-react";
import { EXCELLENCE } from "@/data/content";
import { Reveal, Words } from "@/lib/motion";
import { scrollToId } from "@/components/SmoothScroll";
import Magnetic from "@/components/Magnetic";

const ICONS = { Sparkles, Droplets, BadgeEuro };

export default function Excellence() {
    const { kicker, title, intro, engagements, offer } = EXCELLENCE;

    const claimOffer = () => {
        window.dispatchEvent(new CustomEvent("ign:prefill-service", { detail: offer.service }));
        scrollToId("contact");
    };

    return (
        <section id="excellence" className="py-24 md:py-32 bg-white relative overflow-hidden" data-testid="excellence-section">
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(45% 35% at 85% 0%, rgba(46,123,255,0.07), transparent 70%)" }} />
            <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
                {/* Header */}
                <div className="max-w-3xl mb-16 md:mb-20">
                    <Reveal><span className="uppercase text-xs tracking-[0.2em] font-bold text-brand-red font-body">{kicker}</span></Reveal>
                    <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-navy mt-5 mb-7 font-display leading-[1.05]">
                        <Words text={title} />
                    </h2>
                    <Reveal delay={0.1}><div className="h-px w-16 bg-brand-red mb-8" /></Reveal>
                    {intro.map((p, i) => (
                        <Reveal key={i} delay={0.14 + i * 0.06}>
                            <p className={"text-base md:text-lg leading-relaxed font-body " + (i === 0 ? "text-slate-500 mb-5" : "text-navy font-medium")}>{p}</p>
                        </Reveal>
                    ))}
                </div>

                {/* Engagements */}
                <div className="grid lg:grid-cols-3 gap-px bg-line border border-line mb-16">
                    {engagements.map((e, i) => {
                        const Icon = ICONS[e.icon];
                        return (
                            <Reveal key={e.title} delay={i * 0.12} y={34} className="bg-white p-8 md:p-10 flex flex-col">
                                <div className="flex items-center gap-4 mb-6">
                                    <span className="text-4xl font-light text-line font-display leading-none">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="w-11 h-11 flex items-center justify-center bg-navy text-white flex-shrink-0">
                                        <Icon className="w-5 h-5" />
                                    </span>
                                </div>
                                <h3 className="text-xl font-medium text-navy mb-4 font-display leading-snug">{e.title}</h3>
                                <p className="text-sm leading-relaxed text-slate-500 font-body">{e.text}</p>

                                {e.chips && (
                                    <div className="flex flex-col gap-2.5 mt-6">
                                        {e.chips.map((c) => (
                                            <span key={c} className="inline-flex items-center gap-2 text-sm font-medium text-navy font-body">
                                                <Check className="w-4 h-4 text-electric flex-shrink-0" /> {c}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {e.strong && (
                                    <p className="mt-auto pt-6 text-base text-navy italic font-display border-t border-line mt-6">
                                        « {e.strong} »
                                    </p>
                                )}
                            </Reveal>
                        );
                    })}
                </div>

                {/* Offer banner */}
                <Reveal y={40}>
                    <div className="relative overflow-hidden bg-ink grain" data-testid="welcome-offer">
                        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 120% at 15% 20%, rgba(46,123,255,0.28), transparent 60%)" }} />
                        <div className="absolute top-0 left-0 right-0 h-1 french-stripe" />
                        <div className="relative z-10 p-8 md:p-14 flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-12">
                            <div className="flex-1">
                                <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-electric-soft font-body font-semibold mb-5">
                                    <Star className="w-3.5 h-3.5 fill-electric-soft" /> {offer.badge}
                                </span>
                                <p className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white font-display leading-none mb-4">
                                    {offer.title}
                                </p>
                                <p className="text-white/70 text-base md:text-lg font-body max-w-xl">{offer.subtitle}</p>
                            </div>
                            <div className="flex-shrink-0">
                                <Magnetic strength={0.35}>
                                    <button
                                        onClick={claimOffer}
                                        className="group inline-flex items-center gap-3 bg-electric text-white px-9 py-5 text-sm uppercase tracking-[0.12em] font-semibold font-body hover:bg-white hover:text-navy transition-colors duration-300"
                                        style={{ boxShadow: "0 0 40px rgba(46,123,255,0.45)" }}
                                        data-testid="welcome-offer-btn"
                                    >
                                        {offer.button}
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </Magnetic>
                            </div>
                        </div>
                        <div className="relative z-10 px-8 md:px-14 pb-8">
                            <p className="text-[11px] leading-relaxed text-white/40 font-body max-w-4xl">{offer.conditions}</p>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
